import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Copy, Loader2, Mail, UserMinus, X, Crown } from "lucide-react";

/** v13 · «Mi equipo»: la cuenta madre de un plan Business gestiona a las personas que dependen de ella. */
const input = "h-10 w-full rounded-md border border-brand-line bg-transparent px-3 text-sm text-brand-ink outline-none focus:border-brand-accent";
const btn = "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-accent px-4 text-sm font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60";
const ghost = "inline-flex items-center gap-1.5 rounded-md border border-brand-line px-2.5 py-1 text-xs font-semibold text-brand-muted hover:text-brand-ink disabled:opacity-60";
const card = "rounded-lg border border-brand-line bg-brand-panel p-6";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
};

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Enlace copiado."); } catch { toast.error("No se ha podido copiar. Selecciónalo y cópialo a mano."); }
};

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [prospects, setProspects] = useState(null);
  const [email, setEmail] = useState("");
  const [lastLink, setLastLink] = useState(null);
  const [busy, setBusy] = useState("");
  const [who, setWho] = useState("all");

  const load = useCallback(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch((e) => { toast.error(apiError(e)); setTeam({ role: null }); });
    api.get("/team/prospects").then((r) => setProspects(r.data.items || [])).catch(() => setProspects([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => {
    if (!team?.owner) return [];
    return [{ id: team.owner.id, email: team.owner.email, owner: true }, ...(team.members || [])];
  }, [team]);
  const shown = useMemo(() => (prospects || []).filter((p) => who === "all" || p.owner_email === who), [prospects, who]);

  if (user && user.team_role !== "owner") return <Navigate to="/app/cuenta" replace />;

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Escribe el correo de la persona."); return; }
    setBusy("invite");
    try {
      const r = await api.post("/team/invites", { email: email.trim() });
      setLastLink({ email: r.data.email, link: r.data.link });
      setEmail("");
      toast.success(`Invitación creada para ${r.data.email}. Envíale el enlace.`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const cancelInvite = async (inv) => {
    setBusy(inv.invite_id);
    try { await api.delete(`/team/invites/${inv.invite_id}`); toast.success("Invitación anulada."); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`¿Sacar a ${m.email} del equipo? Conservará su cuenta, pero dejará de usar tu negocio y tu bolsa de búsquedas.`)) return;
    setBusy(m.id);
    try { await api.post(`/team/members/${m.id}/remove`); toast.success(`${m.email} ya no está en el equipo.`); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  if (!team) {
    return <p className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 size={16} className="animate-spin" /> Cargando equipo…</p>;
  }

  const seats = team.seats || {};
  const searches = team.searches || {};
  const pct = Math.min(100, Math.round(((searches.used || 0) / Math.max(1, searches.limit || 1)) * 100));

  return (
    <div className="space-y-8" data-testid="team-page">
      <PageHeader
        title="Mi equipo"
        subtitle="Eres la cuenta madre: todo tu equipo prospecta para tu negocio, cada persona con su propia cuenta y sin que a nadie le salgan contactos que ya se le ofrecieron a otra."
        testId="team-header"
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <section className={card} data-testid="team-seats">
          <p className="text-sm font-semibold text-brand-muted">Plazas</p>
          <p className="mt-1 text-2xl font-extrabold text-brand-ink">{seats.used} de {seats.total}</p>
          <p className="mt-1 text-sm text-brand-muted">
            Incluye tu cuenta. {seats.pending ? `${seats.pending} invitación(es) pendiente(s). ` : ""}
            {seats.free} libre(s).{seats.extra ? ` ${seats.extra} usuario(s) extra contratado(s).` : ""}
          </p>
        </section>
        <section className={card} data-testid="team-searches">
          <p className="text-sm font-semibold text-brand-muted">Bolsa común de búsquedas este mes</p>
          <p className="mt-1 text-2xl font-extrabold text-brand-ink">{searches.used} de {searches.limit}</p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brand-panel2">
            <div className="h-full rounded-full bg-brand-accent" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-brand-muted">La comparte todo el equipo. Se renueva el día 1.</p>
        </section>
      </div>

      <section className={card} data-testid="team-invite">
        <h2 className="text-base font-bold text-brand-ink">Invitar a una persona</h2>
        <p className="mt-0.5 text-sm text-brand-muted">Creamos un enlace personal; envíaselo tú. Caduca en 14 días y solo sirve con ese correo.</p>
        <form onSubmit={invite} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input type="email" className={input} placeholder="correo@empresa.com" value={email}
                 onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" disabled={!seats.free} />
          <button type="submit" className={btn} disabled={busy === "invite" || !seats.free} data-testid="team-invite-submit">
            {busy === "invite" ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Crear invitación
          </button>
        </form>
        {!seats.free && <p className="mt-2 text-xs text-brand-muted">No quedan plazas libres. Escríbenos para añadir usuarios extra.</p>}
        {lastLink && (
          <div className="mt-4 rounded-md border border-brand-accent/40 bg-brand-accent/[0.08] p-3" data-testid="team-invite-link">
            <p className="text-xs font-semibold text-brand-ink">Enlace para {lastLink.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly className={`${input} h-9 text-xs`} value={lastLink.link} onFocus={(e) => e.target.select()} />
              <button type="button" className={ghost} onClick={() => copy(lastLink.link)}><Copy size={13} /> Copiar</button>
            </div>
          </div>
        )}
      </section>

      <section className={card} data-testid="team-people">
        <h2 className="text-base font-bold text-brand-ink">Personas del equipo</h2>
        <ul className="mt-4 divide-y divide-brand-line">
          <li className="flex items-center justify-between py-3 text-sm">
            <span className="flex items-center gap-2 text-brand-ink"><Crown size={14} className="text-brand-accent" /> {team.owner?.email}</span>
            <span className="text-xs font-semibold text-brand-accent">Cuenta madre</span>
          </li>
          {(team.members || []).map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid="team-member">
              <span className="min-w-0 truncate text-brand-ink">{m.name ? `${m.name} · ` : ""}{m.email}
                {m.joined_at && <span className="ml-2 text-xs text-brand-muted">desde {fmtDate(m.joined_at)}</span>}
              </span>
              <button type="button" className={ghost} disabled={busy === m.id} onClick={() => removeMember(m)} data-testid="team-member-remove">
                <UserMinus size={13} /> Sacar del equipo
              </button>
            </li>
          ))}
          {(team.invites || []).map((inv) => (
            <li key={inv.invite_id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid="team-invite-row">
              <span className="min-w-0 truncate text-brand-muted">{inv.email} <span className="text-xs">· invitación pendiente hasta {fmtDate(inv.expires_at)}</span></span>
              <button type="button" className={ghost} disabled={busy === inv.invite_id} onClick={() => cancelInvite(inv)} data-testid="team-invite-cancel">
                <X size={13} /> Anular
              </button>
            </li>
          ))}
        </ul>
        {!(team.members || []).length && !(team.invites || []).length && (
          <p className="mt-2 text-sm text-brand-muted">Todavía no hay nadie más. Invita a tu primera persona arriba.</p>
        )}
      </section>

      <section className={card} data-testid="team-prospects">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-brand-ink">Contactos del equipo</h2>
            <p className="mt-0.5 text-sm text-brand-muted">Solo lectura. Cada persona gestiona los suyos desde su CRM.</p>
          </div>
          <select className={`${input} w-auto`} value={who} onChange={(e) => setWho(e.target.value)} data-testid="team-prospects-filter">
            <option value="all">Todo el equipo</option>
            {people.map((p) => <option key={p.id} value={p.email}>{p.owner ? `${p.email} (tú)` : p.email}</option>)}
          </select>
        </div>
        {prospects === null ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-brand-muted"><Loader2 size={15} className="animate-spin" /> Cargando…</p>
        ) : !shown.length ? (
          <p className="mt-4 text-sm text-brand-muted">No hay contactos todavía.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="text-xs text-brand-muted">
                <tr><th className="py-2 pr-3 font-semibold">Contacto</th><th className="py-2 pr-3 font-semibold">Estado</th>
                  <th className="py-2 pr-3 font-semibold">Siguiente paso</th><th className="py-2 font-semibold">Lo lleva</th></tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {shown.map((p) => (
                  <tr key={p.id} data-testid="team-prospect-row">
                    <td className="py-2 pr-3 text-brand-ink">{p.name || p.username}{p.username && <span className="ml-1 text-xs text-brand-muted">@{p.username}</span>}</td>
                    <td className="py-2 pr-3 text-brand-ink">{p.status_label}</td>
                    <td className="py-2 pr-3 text-brand-muted">{p.next_step || "—"}</td>
                    <td className="py-2 text-brand-muted">{p.mine ? "Tú" : p.owner_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
